<template>
  <div class="company-row" :class="{'inactive': !active}">
    <!-- Status Badge -->
    <div class="row-status">
      <span class="status-badge" :class="statusClass">
        <i :class="statusIcon"></i>
        {{ statusText }}
      </span>
    </div>
    
    <!-- Company Info -->
    <div class="row-company">
      <div class="company-main">
        <p class="company-nif">{{ company.nif_goldylocks }}</p>
        <p class="company-name">{{ company.nome_goldylocks }}</p>
        <a :href="url" target="_blank" class="company-alias">
          <i class="fas fa-external-link-alt mr-1"></i>
          {{ company.alias_goldylocks }}
        </a>
      </div>
      
      <!-- Admin Details -->
      <div v-if="isAdministration" class="company-meta">
        <div class="meta-item" v-if="company.email_utilizador">
          <span class="meta-label">Utilizador</span>
          <span class="meta-value">{{ company.email_utilizador }}</span>
        </div>
        <div class="meta-item" v-if="company.agente_email">
          <span class="meta-label">Agente</span>
          <span class="meta-value">{{ company.agente_email }}</span>
        </div>
        <div class="meta-item" v-if="company.contabilista_email">
          <span class="meta-label">Contabilista</span>
          <span class="meta-value">{{ company.contabilista_email }}</span>
        </div>
      </div>
    </div>
    
    <!-- Plan & Expiry -->
    <div class="row-plan">
      <Plan :company="company"></Plan>
    </div>
    
    <!-- Days Remaining -->
    <div class="row-expiry">
      <p class="expiry-value" :class="expiryClass">
        {{ licenseDaysRemaining || 0 }}
      </p>
      <p class="expiry-label">dias restantes</p>
      <div class="expiry-progress">
        <div class="progress-bar" :class="progressClass" :style="{width: progressWidth}"></div>
      </div>
    </div>
    
    <!-- Actions -->
    <div class="row-actions">
      <router-link :to="detailsLink" class="button is-small" :class="active ? 'is-primary' : 'is-warning'">
        <i class="fas fa-eye mr-1"></i>
        {{ active ? 'Detalhes' : 'Reativar' }}
      </router-link>
      <a v-if="hasAdminAccess" target="_blank" :href="accountantURL" class="button is-small is-info">
        <i class="fas fa-user-tie mr-1"></i>
        TOC
      </a>
    </div>
  </div>
</template>

<script>
import Plan from "@/components/labels/Plan";

export default {
  name: "CompaniesRow",
  components: {Plan},
  props: ['company'],
  computed: {
    isAdministration() {
      return (this.$store.getters.profile.type === "1");
    },
    detailsLink() {
      return `/companies/${this.company.id_goldylocks}`
    },
    url() {
      return `https://app.goldylocks.pt/${this.company.alias_goldylocks}`
    },
    hasAdminAccess() {
      return (typeof this.company.url_hash !== "undefined")
    },
    accountantURL() {
      return `https://app.goldylocks.pt/${this.company.alias_goldylocks}/?hash=${this.company.url_hash}`
    },
    active() {
      return this.company.active;
    },
    licenseDaysRemaining() {
      if (this.company.modules && this.company.modules.vendas) {
        return parseInt(this.company.modules.vendas.remainingDays);
      }
      return 0;
    },
    statusClass() {
      if (!this.active) return 'inactive';
      if (this.licenseDaysRemaining <= 30 && this.licenseDaysRemaining > 0) return 'warning';
      return 'active';
    },
    statusText() {
      if (!this.active) return 'Expirado';
      if (this.licenseDaysRemaining <= 30 && this.licenseDaysRemaining > 0) return 'A expirar';
      return 'Ativo';
    },
    statusIcon() {
      if (!this.active) return 'fas fa-times-circle';
      if (this.licenseDaysRemaining <= 30 && this.licenseDaysRemaining > 0) return 'fas fa-exclamation-triangle';
      return 'fas fa-check-circle';
    },
    expiryClass() {
      if (!this.active || this.licenseDaysRemaining <= 0) return 'danger';
      if (this.licenseDaysRemaining <= 8) return 'danger';
      if (this.licenseDaysRemaining <= 15) return 'warning';
      return 'success';
    },
    progressClass() {
      if (!this.active || this.licenseDaysRemaining <= 0) return 'danger';
      if (this.licenseDaysRemaining <= 8) return 'danger';
      if (this.licenseDaysRemaining <= 15) return 'warning';
      return 'success';
    },
    progressWidth() {
      if (!this.active || this.licenseDaysRemaining <= 0) return '0%';
      if (this.licenseDaysRemaining > 30) return '100%';
      return Math.min(100, (this.licenseDaysRemaining / 30) * 100) + '%';
    }
  }
}
</script>

<style scoped>
.company-row {
  display: grid;
  grid-template-columns: 120px 1fr 120px 100px 180px;
  gap: 16px;
  align-items: center;
  padding: 20px 24px;
  background: var(--surface);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all var(--transition-base);
}

.company-row:hover {
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.company-row.inactive {
  opacity: 0.6;
}

/* Status */
.row-status {
  text-align: center;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: var(--radius-full);
  font-size: 0.75rem;
  font-weight: 600;
}

.status-badge.active {
  background: rgba(16, 185, 129, 0.1);
  color: var(--success);
}

.status-badge.warning {
  background: rgba(245, 158, 11, 0.1);
  color: var(--warning);
}

.status-badge.inactive {
  background: rgba(239, 68, 68, 0.1);
  color: var(--danger);
}

/* Company Info */
.row-company {
  min-width: 0;
}

.company-main {
  margin-bottom: 8px;
}

.company-nif {
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-bottom: 2px;
}

.company-name {
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-primary);
  margin-bottom: 4px;
}

.company-alias {
  font-size: 0.8rem;
  color: var(--primary);
}

.company-alias:hover {
  text-decoration: underline;
}

.company-meta {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #E2E8F0;
  margin-top: 8px;
}

.meta-item {
  display: flex;
  flex-direction: column;
}

.meta-label {
  font-size: 0.7rem;
  color: var(--text-muted);
  text-transform: uppercase;
}

.meta-value {
  font-size: 0.8rem;
  color: var(--text-secondary);
}

/* Plan */
.row-plan {
  text-align: center;
}

/* Expiry */
.row-expiry {
  text-align: center;
}

.expiry-value {
  font-size: 1.5rem;
  font-weight: 700;
  line-height: 1;
}

.expiry-value.success { color: var(--success); }
.expiry-value.warning { color: var(--warning); }
.expiry-value.danger { color: var(--danger); }

.expiry-label {
  font-size: 0.7rem;
  color: var(--text-muted);
  margin-top: 4px;
}

.expiry-progress {
  margin-top: 8px;
  height: 4px;
  background: #E2E8F0;
  border-radius: 2px;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width var(--transition-base);
}

.progress-bar.success { background: var(--success); }
.progress-bar.warning { background: var(--warning); }
.progress-bar.danger { background: var(--danger); }

/* Actions */
.row-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: stretch;
  flex-wrap: nowrap;
}

.row-actions .button {
  width: 100%;
  justify-content: center;
}

.row-actions .button.is-warning {
  background: linear-gradient(135deg, var(--warning) 0%, #D97706 100%);
  border: none;
  color: white;
}

.row-actions .button.is-warning:hover {
  background: linear-gradient(135deg, #D97706 0%, #B45309 100%);
}

@media only screen and (max-width: 1024px) {
  .company-row {
    grid-template-columns: 1fr;
    gap: 16px;
  }
  
  .row-status, .row-plan, .row-expiry {
    text-align: left;
  }
  
  .row-actions {
    flex-direction: column;
    gap: 8px;
    align-items: stretch;
  }
  
  .row-actions .button {
    width: 100%;
  }
}
</style>
